import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { promptsApi } from "@/api/prompts";
import type { SavePromptOverrideRequest } from "@/types/prompts";
import { promptKeys } from "./promptKeys";

export function usePromptCatalog() {
  return useQuery({
    queryKey: promptKeys.catalog(),
    queryFn: () => promptsApi.catalog(),
  });
}

export function usePromptConfigurations() {
  return useQuery({
    queryKey: promptKeys.configurations(),
    queryFn: () => promptsApi.configurations(),
  });
}

export function usePromptReconciliation() {
  return useQuery({
    queryKey: promptKeys.reconciliation(),
    queryFn: () => promptsApi.reconciliation(),
  });
}

/** Copy-only backfill of the legacy prompt tables into the unified store. On
 *  success every prompt read (catalog / configurations / reconciliation) changes,
 *  so invalidate the whole surface by prefix. */
export function useCopyPromptsFromLegacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => promptsApi.copyFromLegacy(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: promptKeys.all });
    },
  });
}

/** The full editor payload for one call site (catalog default + current global
 *  override). Disabled until a key is selected. */
export function usePromptDefinition(key: string | null) {
  return useQuery({
    queryKey: promptKeys.definition(key ?? ""),
    queryFn: () => promptsApi.definition(key as string),
    enabled: key != null,
  });
}

/** Upsert a call site's global override. Changes the catalog "Overridden" badge
 *  and the unified-store rows, so invalidate the whole surface by prefix. */
export function useSaveOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }: { key: string; body: SavePromptOverrideRequest }) =>
      promptsApi.saveOverride(key, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: promptKeys.all });
    },
  });
}

/** Remove a call site's global override (reset to the catalog default). */
export function useDeleteOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => promptsApi.deleteOverride(key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: promptKeys.all });
    },
  });
}
